package com.bikompanion.ui.home

import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import kotlinx.coroutines.launch
import com.bikompanion.domain.BikeStation
import com.bikompanion.ui.link.StationLinks
import com.bikompanion.ui.strings.LocalStrings

/**
 * What a share button does.
 *
 * The host's own share sheet goes first, through [onShareLink], which answers whether it took the
 * link. Where it has none (desktop) or declines (a browser without the Web Share API), the link is
 * copied instead and a toast says so, since a button that does nothing visible reads as broken.
 *
 * Uses the deprecated `LocalClipboardManager` because its replacement, `LocalClipboard`, takes a
 * `ClipEntry`, which has no common way to be built from text on this Compose version: it is made
 * per platform, and this fallback is shared code.
 */
@Suppress("DEPRECATION")
@Composable
internal fun rememberLinkShare(
    onShareLink: ((title: String, url: String) -> Boolean)?,
    snackbarHostState: SnackbarHostState,
): (title: String, url: String) -> Unit {
    val clipboard = LocalClipboardManager.current
    val scope = rememberCoroutineScope()
    val strings = LocalStrings.current
    val currentOnShareLink by rememberUpdatedState(onShareLink)
    return remember(clipboard, snackbarHostState, strings) {
        { title, url ->
            if (currentOnShareLink?.invoke(title, url) != true) {
                scope.launch {
                    clipboard.setText(AnnotatedString(url))
                    snackbarHostState.showSnackbar(strings.linkCopied)
                }
            }
        }
    }
}

/** Shares a link to a station, built on [baseUrl]. */
@Composable
internal fun rememberStationShare(
    baseUrl: String,
    onShareLink: ((title: String, url: String) -> Boolean)?,
    snackbarHostState: SnackbarHostState,
): (BikeStation) -> Unit {
    val shareLink = rememberLinkShare(onShareLink, snackbarHostState)
    return remember(baseUrl, shareLink) {
        { station ->
            shareLink(station.name, StationLinks.url(baseUrl, StationFocusTarget(station.networkId, station.id)))
        }
    }
}
